import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, User } from 'lucide-react';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { useTheme } from '../context/ThemeContext';
import SplitSlideHeader from './SplitSlideHeader';
import { UserProfile } from './UserProfile';

interface NavLink {
  label: string;
  href: string;
}

interface ModernStickyHeaderProps {
  logo?: string;
  navLinks?: NavLink[];
  onNavigationStart?: () => void;
}

const defaultNavLinks: NavLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Projects', href: '/projects' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/#contact' },
];

export function ModernStickyHeader({ 
  logo = 'Sean', 
  navLinks = defaultNavLinks,
  onNavigationStart
}: ModernStickyHeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollDirection = useScrollDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  const isAtTop = typeof window !== 'undefined' ? window.scrollY < 100 : true;

  // Handle scroll state
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle escape key for mobile menu and dropdown
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      if (!isMobileMenuOpen) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isMobileMenuOpen]);

  // Handle click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToSection = (href: string) => {
    setIsMobileMenuOpen(false);
    
    // Call the navigation start callback immediately
    if (onNavigationStart) {
      onNavigationStart();
    }
    
    if (href.startsWith('#')) {
      // If we're on the homepage, scroll to section
      if (location.pathname === '/') {
        if (href === '#home') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        
        const element = document.querySelector(href);
        if (element) {
          const offset = 80; // Account for sticky header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        // If we're on another page, navigate to homepage with hash
        navigate(`/${href}`);
      }
    } else {
      // Handle route navigation
      navigate(href);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: 0 }}
        animate={{ y: 0 }}
        className={`fixed top-0 w-full z-50 h-18 backdrop-blur-sm bg-white/80 dark:bg-gray-900/80 border-b border-black/10 dark:border-white/10 transition-shadow duration-300 ${
          isScrolled ? 'shadow-sm' : ''
        }`}
      >
        <div className="h-full flex justify-between items-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          {/* Logo/Brand */}
          <Link 
            to="/"
            className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <SplitSlideHeader delay={500} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-2">
            {navLinks.map((link) => {
              const isActive = false; // You can implement active detection based on scroll position
              return (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                    className={`
                    relative px-5 py-2 text-sm font-medium transition-all duration-300
                    ${isActive 
                      ? 'text-gray-900 dark:text-white' 
                      : 'text-gray-900 dark:text-white hover:text-gray-700 dark:hover:text-gray-300'
                    }
                    group
                  `}
                >
                  <span className="relative z-10">{link.label}</span>
                  
                  {/* Modern underline effect */}
                  <span className={`
                    absolute bottom-0 left-0 w-full h-0.5 
                    bg-gradient-to-r from-orange-500 to-amber-500
                    transform origin-left transition-transform duration-300
                    ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}
                  `} />
                  
                  {/* Subtle background on hover */}
                  <span className="absolute inset-0 bg-gray-50 dark:bg-gray-800 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-300 -z-10" />
                </button>
              );
            })}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Sign In Button - Circular Icon with Dropdown */}
            <div className="relative">
              {/* User Profile Component */}
              <UserProfile />
            </div>

            {/* Theme Toggle */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.1, rotate: 15 }}
              onClick={toggleTheme}
              className="p-2 transition-colors focus:outline-none"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              )}
            </motion.button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={handleBackdropClick}
            />

            {/* Menu Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="fixed right-0 top-0 h-screen w-80 bg-white dark:bg-gray-900 shadow-xl z-50"
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-light text-gray-900 dark:text-white">Menu</h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors focus:outline-none"
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                  </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 overflow-y-auto">
                  <motion.div
                    initial="closed"
                    animate="open"
                    variants={{
                      open: {
                        transition: { staggerChildren: 0.07, delayChildren: 0.1 }
                      },
                      closed: {
                        transition: { staggerChildren: 0.05, staggerDirection: -1 }
                      }
                    }}
                    className="py-4"
                  >
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.href}
                        variants={{
                          open: {
                            y: 0,
                            opacity: 1,
                            transition: {
                              y: { stiffness: 1000, velocity: -100 }
                            }
                          },
                          closed: {
                            y: 20,
                            opacity: 0,
                            transition: {
                              y: { stiffness: 1000 }
                            }
                          }
                        }}
                      >
                        <button
                          onClick={() => scrollToSection(link.href)}
                          className="w-full text-left py-4 px-6 text-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                        >
                          {link.label}
                        </button>
                      </motion.div>
                    ))}
                  </motion.div>
                </nav>

                {/* Theme Toggle in Mobile */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                  <motion.button
                    onClick={toggleTheme}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800 transition-colors focus:outline-none"
                  >
                    <span className="text-gray-700 dark:text-gray-300">
                      {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </span>
                    {theme === 'dark' ? (
                      <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    ) : (
                      <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
